import { useEffect, useState } from "react";
import { Autocomplete, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { customersApi, type CustomerDto } from "@/api/endpoints";

function useDebouncedValue(value: string, delay = 250) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const h = setTimeout(() => setD(value), delay);
    return () => clearTimeout(h);
  }, [value, delay]);
  return d;
}

/** A mobile number, roughly — enough digits that it isn't a UHID fragment. */
function looksLikePhone(s: string): boolean {
  const digits = s.replace(/\D/g, "");
  return digits.length >= 6 && /^[\d\s+()-]+$/.test(s);
}

/**
 * Patient lookup that accepts a **UHID or a mobile number**. Everywhere staff identify a patient
 * they have whichever the patient gives them, and making them know which box to type into is how
 * the wrong one gets typed.
 *
 * It searches the stored patient projection (customers carry both UHID and phone) and hands back
 * the UHID, which is what every downstream lookup keys on. Free text is passed through unchanged,
 * so a UHID for a patient with no customer record yet — a HealNTrack patient never billed here —
 * still works exactly as before.
 */
export function PatientUhidField({
  value, onChange, onCommit, label = "UHID or mobile", required, disabled, helperText, sx,
}: {
  /** The UHID. */
  value: string;
  onChange: (uhid: string) => void;
  /** Fired when the user settles on a value (picked an option, blurred, or pressed Enter). */
  onCommit?: (uhid: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  sx?: object;
}) {
  const [input, setInput] = useState(value);
  const debounced = useDebouncedValue(input);

  // Keep the box in step when the parent resets or prefills it.
  useEffect(() => { setInput(value); }, [value]);

  const q = useQuery({
    queryKey: ["patient-uhid-lookup", debounced],
    queryFn: () => customersApi.list({ search: debounced.trim(), pageSize: 10 }),
    // Two characters is noise; a phone or UHID fragment is worth a search.
    enabled: debounced.trim().length >= 3,
    staleTime: 60_000,
  });

  // Only patients (a UHID) can be handed to a UHID-keyed lookup.
  const options = (q.data?.items ?? []).filter(c => !!c.uhid?.trim());

  const commit = (uhid: string) => {
    onChange(uhid);
    onCommit?.(uhid);
  };

  return (
    <Autocomplete<CustomerDto, false, false, true>
      freeSolo
      disabled={disabled}
      options={options}
      inputValue={input}
      onInputChange={(_, v) => { setInput(v); onChange(v); }}
      onChange={(_, v) => {
        // Picking a suggestion always yields its UHID, even when the user searched by phone.
        const uhid = typeof v === "string" ? v : (v?.uhid ?? "");
        setInput(uhid);
        commit(uhid);
      }}
      onBlur={() => commit(input.trim())}
      filterOptions={(x) => x}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.uhid ?? "")}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      size="small"
      sx={sx}
      renderOption={(props, o) => (
        <li {...props} key={o.id}>
          <Stack spacing={0}>
            <Typography variant="body2">{o.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {o.uhid}{o.phone ? ` · ${o.phone}` : ""}
            </Typography>
          </Stack>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={`${label}${required ? " *" : ""}`}
          placeholder="UHID or mobile number"
          helperText={
            helperText
            ?? (looksLikePhone(input) && options.length === 0 && !q.isFetching && debounced.trim().length >= 3
              ? "No patient found with that mobile number"
              : undefined)
          }
          onKeyDown={(e) => { if (e.key === "Enter") commit(input.trim()); }}
          InputProps={{
            ...params.InputProps,
            endAdornment: <>
              {q.isFetching ? <CircularProgress size={16} /> : null}
              {params.InputProps.endAdornment}
            </>,
          }}
        />
      )}
    />
  );
}
